import {
    CampaignDelivery,
    CampaignDeliveryInput,
    CampaignImage,
    CampaignImageInput,
    CampaignInquiryProduct,
    CampaignInquiryProductInfo,
    CampaignInquiryProductInfoInput,
    CampaignInquiryProductInput,
    CampaignInquirySettings,
    CampaignInquirySettingsInput,
    CampaignOutcome,
    CampaignOutcomeInput,
    CampaignPricing,
    CampaignRestrictions,
    CampaignRestrictionsInput,
    CampaignTag,
    CampaignTagInput,
    CreateCampaignInquiryInput,
    CreateCampaignPricingInput,
    FileInput,
    ProductAttribute,
    ProductAttributeInput,
    ProductCategory,
    ProductCategoryInput,
    ProductLink,
    ProductLinkInput,
    Question,
    QuestionCondition,
    QuestionConditionInput,
    QuestionCriteria,
    QuestionCriteriaInput,
    QuestionInput,
    Questionnaire,
    QuestionnaireInput,
    QuestionOption,
    QuestionOptionInput,
    ReviewDestination,
    ReviewDestinationConfigField,
    ReviewDestinationConfigFieldInput,
    ReviewDestinationInput,
    RichText,
    RichTextInput,
    Tenant,
} from "src/graphql/generated/graphql";
import { CampaignInquiryQueryDataType } from "src/hooks/campaignInquiries";
import { CampaignQueryDataType } from "src/hooks/campaigns";
import { DeepExplicitPartial } from "src/types/utils";

export const mapDisplayTenants = (tenants: Tenant[]): string[] => {
    const tenantIds: string[] = [];
    tenants.forEach((tenant) => {
        if (tenant.id) {
            tenantIds.push(tenant.id);
        }
    });

    return tenantIds;
};

export const mapImage = (image: FileInput): DeepExplicitPartial<FileInput> => {
    return {
        id: image.id ?? undefined,
        src: image.src ?? undefined,
        objectKey: image.objectKey ?? undefined,
    };
};

export const mapImages = (images: FileInput[]): DeepExplicitPartial<FileInput>[] => {
    return images.map((image) => mapImage(image));
};

export const mapFileImages = (images: CampaignImage[]): DeepExplicitPartial<CampaignImageInput>[] => {
    return images.map((image) => ({
        file: image.file ? mapImage(image.file) : undefined,
    }));
};

export const mapRestrictions = (restrictions: CampaignRestrictions): DeepExplicitPartial<CampaignRestrictionsInput> => {
    const location = {
        city: restrictions.location?.city ?? undefined,
        country: restrictions.location?.country ?? undefined,
        line1: restrictions.location?.line1 ?? undefined,
        line2: restrictions.location?.line2 ?? undefined,
        postalCode: restrictions.location?.postalCode ?? undefined,
    };
    return {
        ageMax: restrictions.ageMax ?? undefined,
        ageMin: restrictions.ageMin ?? undefined,
        gender: restrictions.gender ?? undefined,
        location,
    };
};

export const mapInstructions = (instructions: RichText): DeepExplicitPartial<RichTextInput> => {
    return {
        images: instructions.images ? mapImages(instructions.images) : undefined,
        json: instructions.json ?? undefined,
    };
};

export const mapDelivery = (delivery: CampaignDelivery): DeepExplicitPartial<CampaignDeliveryInput> => {
    return {
        freeOfCharge: delivery.freeOfCharge ?? undefined,
        instructions: delivery.instructions ? mapInstructions(delivery.instructions) : undefined,
        notes: delivery.notes ?? undefined,
        type: delivery.type ?? undefined,
    };
};

export const mapOutcome = (outcome: CampaignOutcome): DeepExplicitPartial<CampaignOutcomeInput> => {
    return {
        fbMentions: outcome.fbMentions ?? undefined,
        hashtags: outcome.hashtags ?? undefined,
        igMentions: outcome.igMentions ?? undefined,
        includeImages: outcome.includeImages ?? undefined,
        includeSocialPosts: outcome.includeSocialPosts ?? undefined,
        includeVideo: outcome.includeVideo ?? undefined,
    };
};

export const mapPricing = (pricing: CampaignPricing): DeepExplicitPartial<CreateCampaignPricingInput> => {
    return {
        discountCodes: pricing.discountCodes?.items?.map((item) => item.code) ?? undefined,
        discountPercent: pricing.discountPercent ?? undefined,
        isFree: pricing.isFree ?? undefined,
    };
};

export const mapAttributes = (attributes: ProductAttribute[]): DeepExplicitPartial<ProductAttributeInput>[] => {
    return attributes.map((attribute) => ({
        id: attribute.id ?? undefined,
        name: attribute.name ?? undefined,
        locale: attribute.locale ?? undefined,
    }));
};

export const mapCategory = (category: ProductCategory): DeepExplicitPartial<ProductCategoryInput> => {
    return {
        key: category.key ?? undefined,
        parent: category.parent ? mapCategory(category.parent) : undefined,
    };
};

export const mapConfigFields = (
    configFields: ReviewDestinationConfigField[]
): DeepExplicitPartial<ReviewDestinationConfigFieldInput>[] => {
    return configFields.map((configField) => ({
        isRequiredForApproval: configField.isRequiredForApproval ?? undefined,
        label: configField.label ?? undefined,
        path: configField.path ?? undefined,
        placeholder: configField.placeholder ?? undefined,
        type: configField.type ?? undefined,
        urlStripQueryString: configField.urlStripQueryString ?? undefined,
    }));
};

export const mapDestinations = (destinations: ReviewDestination[]): DeepExplicitPartial<ReviewDestinationInput>[] => {
    return destinations.map((destination) => ({
        config: destination.config ?? undefined,
        configFields: destination.configFields ? mapConfigFields(destination.configFields) : undefined,
        key: destination.key ?? undefined,
        name: destination.name ?? undefined,
        type: destination.type ?? undefined,
        isOrderNumber: destination.isOrderNumber ?? undefined,
    }));
};

export const mapDetailPageLinks = (detailPageLinks: ProductLink[]): DeepExplicitPartial<ProductLinkInput>[] => {
    return detailPageLinks.map((detailPageLink) => ({
        label: detailPageLink.label ?? undefined,
        src: detailPageLink.src ?? undefined,
    }));
};

export const mapInfo = (info: CampaignInquiryProductInfo): DeepExplicitPartial<CampaignInquiryProductInfoInput> => {
    return {
        manufacturer: info.manufacturer ?? undefined,
        model: info.model ?? undefined,
    };
};

export const mapProducts = (products: CampaignInquiryProduct[]): DeepExplicitPartial<CampaignInquiryProductInput>[] => {
    return products.map((product) => ({
        attributes: product.attributes ? mapAttributes(product.attributes) : undefined,
        category: product.category ? mapCategory(product.category) : undefined,
        description: product.description ? mapInstructions(product.description) : undefined,
        destinations: product.destinations ? mapDestinations(product.destinations) : undefined,
        detailPageLinks: product.detailPageLinks ? mapDetailPageLinks(product.detailPageLinks) : undefined,
        ean: product.ean ?? undefined,
        id: product.id ?? undefined,
        images: product.images ? mapImages(product.images) : undefined,
        info: product.info ? mapInfo(product.info) : undefined,
        key: product.key ?? undefined,
        name: product.name ?? undefined,
        reviewFocusInstructions: product.reviewFocusInstructions
            ? mapInstructions(product.reviewFocusInstructions)
            : undefined,
        thumbnailImage: product.thumbnailImage ? mapImage(product.thumbnailImage) : undefined,
        userManualLink: product.userManualLink ?? undefined,
    }));
};

export const mapCriteria = (criteria: QuestionCriteria[]): DeepExplicitPartial<QuestionCriteriaInput>[] => {
    return criteria.map((criterion) => ({
        dependsOn: criterion.dependsOn ?? undefined,
        note: criterion.note ?? undefined,
        requiredValue: criterion.requiredValue ?? undefined,
        type: criterion.type ?? undefined,
    }));
};

export const mapCondition = (condition: QuestionCondition): DeepExplicitPartial<QuestionConditionInput> => {
    return {
        dependsOn: condition.dependsOn ?? undefined,
        requiredValue: condition.requiredValue ?? undefined,
        type: condition.type ?? undefined,
    };
};

export const mapOptions = (
    options: QuestionOption[],
    parentKey: string
): DeepExplicitPartial<QuestionOptionInput>[] => {
    return options.map((option) => {
        const key = option.key ?? `${parentKey}::${Math.random().toString(36).substring(2)}`;

        return {
            key,
            text: option.text ?? undefined,
        };
    });
};

export const mapQuestions = (questions: Question[]): DeepExplicitPartial<QuestionInput>[] => {
    return questions.map((question) => {
        const key = question.key ?? `:CUSTOM::${Math.random().toString(36).substring(2)}`;

        return {
            condition: question.condition ? mapCondition(question.condition) : undefined,
            isOptional: question.isOptional ?? undefined,
            key: key,
            options: question.options ? mapOptions(question.options, key) : undefined,
            text: question.text ?? undefined,
            type: question.type ?? undefined,
        };
    });
};

export const mapQuestionnaire = (questionnaire: Questionnaire): DeepExplicitPartial<QuestionnaireInput> => {
    return {
        criteria: questionnaire.criteria ? mapCriteria(questionnaire.criteria) : undefined,
        id: undefined,
        questions: questionnaire.questions ? mapQuestions(questionnaire.questions) : undefined,
    };
};

export const mapSettings = (
    settings: Omit<CampaignInquirySettings, "__typename">
): DeepExplicitPartial<CampaignInquirySettingsInput> => {
    return {
        capacity: settings.capacity ?? undefined,
        openAt: settings.openAt ?? undefined,
    };
};

export const mapTags = (tags: CampaignTag[]): DeepExplicitPartial<CampaignTagInput>[] => {
    return tags.map((tag) => ({
        color: tag.color ?? undefined,
        key: tag.key ?? undefined,
        text: tag.text ?? undefined,
    }));
};

export const mapInquiry = (inquiry: CampaignInquiryQueryDataType): DeepExplicitPartial<CreateCampaignInquiryInput> => {
    return {
        clientId: inquiry.client?.id ?? undefined,
        additionalNotes: inquiry.additionalNotes ?? undefined,
        delivery: inquiry.delivery ? mapDelivery(inquiry.delivery) : undefined,
        description: inquiry.description ? mapInstructions(inquiry.description) : undefined,
        displayTenantIds: inquiry.displayTenants ? mapDisplayTenants(inquiry.displayTenants) : undefined,
        images: inquiry.images ? mapFileImages(inquiry.images) : undefined,
        mainTenantId: inquiry.mainTenant?.id ?? undefined,
        name: inquiry.name ?? undefined,
        outcome: inquiry.outcome ? mapOutcome(inquiry.outcome) : undefined,
        pricing: inquiry.pricing ? mapPricing(inquiry.pricing) : undefined,
        products: inquiry.products ? mapProducts(inquiry.products) : undefined,
        questionnaire: inquiry.questionnaire ? mapQuestionnaire(inquiry.questionnaire) : undefined,
        restrictions: inquiry.restrictions ? mapRestrictions(inquiry.restrictions) : undefined,
        settings: inquiry.settings ? mapSettings(inquiry.settings) : undefined,
        tags: inquiry.tags ? mapTags(inquiry.tags) : undefined,
        tenantId: inquiry.tenant.id ?? undefined,
        thumbnailImage: inquiry.thumbnailImage ? mapImage(inquiry.thumbnailImage) : undefined,
    };
};

export const mapCampaignProductsToInquiryProducts = (
    products: CampaignQueryDataType["products"],
    productReviewDestinations?: CampaignQueryDataType["productReviewDestinations"],
    areProductsNew?: boolean
): DeepExplicitPartial<CampaignInquiryProductInput>[] => {
    return products.map((product) => {
        const destinations = productReviewDestinations?.find(
            (destination) => destination.productId === product.id
        )?.destinations;
        return {
            attributes: product.attributes ? mapAttributes(product.attributes) : undefined,
            category: product.category ? mapCategory(product.category) : undefined,
            description: product.description ? mapInstructions(product.description) : undefined,
            destinations: destinations ? mapDestinations(destinations) : undefined,
            detailPageLinks: product.detailPageLinks ? mapDetailPageLinks(product.detailPageLinks) : undefined,
            ean: product.codes?.ean ?? undefined,
            id: areProductsNew ? undefined : (product.id ?? undefined),
            images: product.images ? mapImages(product.images) : undefined,
            info: product.info
                ? {
                      manufacturer: product.info.manufacturer?.name ?? undefined,
                      model: product.info.model ?? undefined,
                  }
                : undefined,
            key: Math.random().toString(36).substring(2),
            name: product.name ?? undefined,
            reviewFocusInstructions: product.reviewFocusInstructions
                ? mapInstructions(product.reviewFocusInstructions)
                : undefined,
            thumbnailImage: product.thumbnailImage ? mapImage(product.thumbnailImage) : undefined,
            userManualLink: product.userManualLink ?? undefined,
        };
    });
};

export const mapCampaign = (
    campaign: CampaignQueryDataType,
    areProductsNew?: boolean
): DeepExplicitPartial<CreateCampaignInquiryInput> => {
    return {
        clientId: campaign.client?.id ?? undefined,
        additionalNotes: campaign.clientNote ?? undefined,
        delivery: campaign.delivery ? mapDelivery(campaign.delivery) : undefined,
        description: campaign.description ? mapInstructions(campaign.description) : undefined,
        displayTenantIds: campaign.displayTenants ? mapDisplayTenants(campaign.displayTenants) : undefined,
        images: campaign.images ? mapFileImages(campaign.images) : undefined,
        mainTenantId: campaign.mainTenant?.id ?? undefined,
        name: campaign.name ?? undefined,
        outcome: campaign.outcome ? mapOutcome(campaign.outcome) : undefined,
        pricing: campaign.pricing ? mapPricing(campaign.pricing) : undefined,
        products: campaign.products
            ? mapCampaignProductsToInquiryProducts(
                  campaign.products,
                  campaign.productReviewDestinations,
                  areProductsNew
              )
            : undefined,
        questionnaire: campaign.questionnaire ? mapQuestionnaire(campaign.questionnaire) : undefined,
        restrictions: campaign.restrictions ? mapRestrictions(campaign.restrictions) : undefined,
        settings: campaign.settings ? mapSettings(campaign.settings) : undefined,
        tags: campaign.tags ? mapTags(campaign.tags) : undefined,
        tenantId: campaign.tenant.id ?? undefined,
        thumbnailImage: campaign.thumbnailImage ? mapImage(campaign.thumbnailImage) : undefined,
    };
};
